import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { IntegrityScore } from '../components/IntegrityScore';
import { RiskBadge } from '../components/RiskBadge';
import { SkeletonList } from '../components/Skeletons';
import { History as HistoryIcon, Clock, Eye, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const History = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/candidates`, { timeout: 15000 });
      setCandidates(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, candidateId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/candidates/${candidateId}`, { timeout: 10000 });
      setCandidates(candidates.filter(c => c.id !== candidateId));
      toast.success('Análise removida');
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error('Erro ao remover análise');
    }
  };

  const handleView = (candidate) => {
    navigate('/', { state: { candidate } });
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data indisponível';
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center py-6 md:py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-[#0F1C2E] tracking-tight mb-3" data-testid="history-title">
          Histórico de Análises
        </h1>
        <p className="text-base text-[#475569]">
          Todas as análises de candidatos realizadas
        </p>
      </div>

      <Card className="p-5 md:p-6 bg-white border border-[#E2E8F0] rounded-md">
        <div className="flex items-center gap-3 mb-5">
          <HistoryIcon className="w-5 h-5 text-[#1E3A5F]" />
          <h3 className="text-lg font-bold text-[#0F1C2E]">
            {loading ? '...' : `${candidates.length} ${candidates.length === 1 ? 'Análise' : 'Análises'}`}
          </h3>
        </div>

        {loading && <SkeletonList count={4} />}

        {!loading && candidates.length === 0 && (
          <div className="text-center py-12" data-testid="history-empty">
            <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-[#94A3B8]" />
            </div>
            <h3 className="text-lg font-bold text-[#0F1C2E] mb-2">
              Nenhuma Análise Realizada
            </h3>
            <p className="text-sm text-[#475569] mb-4">
              Seu historico de analises aparecera aqui.
            </p>
            <Button
              onClick={() => navigate('/')}
              data-testid="go-to-search-btn"
              className="bg-[#1E3A5F] hover:bg-[#152B47] text-white"
            >
              <Search className="w-4 h-4 mr-2" /> Buscar Candidato
            </Button>
          </div>
        )}

        {!loading && candidates.length > 0 && (
          <div className="space-y-3">
            {candidates.map((candidate) => (
              <div
                key={candidate.id}
                data-testid={`history-item-${candidate.id}`}
                className="p-4 md:p-5 border border-[#E2E8F0] rounded-md hover:border-[#1E3A5F] transition-all duration-200 cursor-pointer group"
                onClick={() => handleView(candidate)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-bold text-[#0F1C2E] truncate">{candidate.name}</h4>
                    <p className="text-sm text-[#475569]">{candidate.position} - {candidate.state}</p>
                    <div className="flex items-center gap-2 text-xs text-[#94A3B8] mt-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(candidate.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4">
                    <IntegrityScore score={candidate.integrity_score} size="sm" />
                    <RiskBadge risk={candidate.risk_classification} />
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#1E3A5F] hover:bg-[#F4F6F8] hidden sm:flex"
                        data-testid={`view-btn-${candidate.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Ver
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDelete(e, candidate.id)}
                        className="text-[#C62828] hover:bg-[#FFEBEE]"
                        data-testid={`delete-btn-${candidate.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default History;
